import { useState, useEffect } from "react";
import { fetchGroupSummary } from "Api/apiService";

/**
 * Manages the group profile summary lifecycle for the Group Skills Map page.
 *
 * If a summary was pre-fetched upstream and passed via router state it is
 * used directly — no network call is made.  The fallback fetch runs only
 * when the page is navigated to directly (e.g. via a bookmarked URL).
 *
 * @param {Array}        selectedEmployees - Employees whose IDs are sent to the API.
 * @param {string|null}  preloadedSummary  - Summary already in router state (or null).
 *
 * @returns {{ summary: object|null, isLoading: boolean }}
 */
const useGroupProfileSummary = (selectedEmployees, preloadedSummary) => {
  const [summary,   setSummary]   = useState(preloadedSummary ?? null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Skip fetch if summary is already available from router state.
    if (summary !== null || !selectedEmployees.length) return;

    const employeeIds = selectedEmployees
      .map((e) => e.employee_id)
      .filter(Boolean);

    if (!employeeIds.length) return;

    setIsLoading(true);
    fetchGroupSummary(employeeIds)
      .then((data) => setSummary(data?.group_summary ?? null))
      .catch((err) => console.error("useGroupProfileSummary: fetch failed —", err))
      .finally(() => setIsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { summary, isLoading };
};

export default useGroupProfileSummary;
